import React, { useState, useEffect } from 'react';
import { FaPercentage, FaMoneyBillWave, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';

const VendorCommissionCard = ({ vendorId, onNavigateToCommission }) => {
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAmount, setShowAmount] = useState(true);
  
  const {
    totalCommissionEarned,
    totalWalletBalance,
    driverWallets,
  } = useSelector((state) => state.vendorWallet);

  useEffect(() => {
    if (vendorId) {
      fetchCommissionRate();
    }
  }, [vendorId]);

  const fetchCommissionRate = async () => {
    try {
      const token = localStorage.getItem('vendorToken');
      const response = await axios.get(
        `${API_URL}/vendor/${vendorId}/commission/settings`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success || response.data.status) {
        setCommissionData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching commission rate:', error);
      // Use a default rate if API fails
      setCommissionData({
        commissionSettings: { effectiveRate: 15 },
        commissionStructure: { adminRate: 10, vendorRate: 15, driverRate: 75 }
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? `${num}%` : 'N/A';
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100 animate-pulse">
        <div className="h-6 w-40 bg-gray-200 rounded mb-4"></div>
        <div className="h-20 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const currentRate = commissionData?.commissionSettings?.effectiveRate ||
                     commissionData?.commissionStructure?.vendorRate || 15;
  const commissionAmount = totalCommissionEarned || 0; // Show real commission earned

  return (
    <div className="bg-gradient-to-br from-green-50 to-emerald-100 rounded-xl shadow-lg p-6 border border-green-200">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-3 rounded-full bg-green-100 text-green-600 mr-3">
            <FaPercentage className="w-6 h-6" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Your Commission</h3>
            <p className="text-sm text-gray-600">Per completed ride</p>
          </div>
        </div>
        <button
          onClick={() => setShowAmount(!showAmount)}
          className="p-2 rounded-full hover:bg-green-200 transition-colors"
          title={showAmount ? "Hide amount" : "Show amount"}
        >
          {showAmount ? (
            <FaEye className="w-4 h-4 text-green-600" />
          ) : (
            <FaEyeSlash className="w-4 h-4 text-green-600" />
          )}
        </button>
      </div>

      {/* Commission Rate Display */}
      <div className="text-center mb-6">
        <div className="text-5xl font-bold text-green-900 mb-2">
          {formatPercentage(currentRate)}
        </div>
        <p className="text-lg text-green-700 font-medium">Per completed ride</p>
      </div>

      {/* Total Commission Earned */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-green-200 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Total Commission Earned</span>
          <FaMoneyBillWave className="w-4 h-4 text-green-500" />
        </div>
        <div className="flex items-center mt-2">
          <span className="text-2xl font-bold text-green-600">
            {showAmount ? formatCurrency(commissionAmount) : "₹****"}
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          From {driverWallets?.length || 0} drivers
        </p>
      </div>

      {/* Action Button */}
      <button
        onClick={() => onNavigateToCommission && onNavigateToCommission('commission')}
        className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center"
      >
        <FaPercentage className="mr-2" />
        View Commission Details
      </button>

      {/* Commission Structure Info */}
      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
        <h4 className="text-sm font-medium text-green-800 mb-2">Commission Breakdown</h4>
        <div className="text-xs text-green-700 space-y-1">
          <div className="flex justify-between">
            <span>Your Commission:</span>
            <span className="font-bold text-green-900">{formatPercentage(currentRate)}</span>
          </div>
          <div className="flex justify-between">
            <span>Admin Platform Fee:</span>
            <span>{formatPercentage(commissionData?.commissionStructure?.adminRate || 10)}</span>
          </div>
          <div className="flex justify-between">
            <span>Driver Gets:</span>
            <span>{formatPercentage(commissionData?.commissionStructure?.driverRate || (100 - currentRate - 10))}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorCommissionCard;
